import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';

const TimerContext = createContext(null);
export const useTimer = () => useContext(TimerContext);

const authHeader = () => {
  const t = localStorage.getItem('uh_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const TimerProvider = ({ children }) => {
  const { user } = useAuth();
  const [timer, setTimer] = useState(null);   // server snapshot
  const [tick, setTick] = useState(0);        // forces 1Hz re-render
  const tickRef = useRef(null);

  // Compute live elapsed seconds from server snapshot + clock.
  const liveElapsedSeconds = (() => {
    if (!timer) return 0;
    const started = new Date(timer.started_at).getTime();
    const totalPaused = (timer.total_paused_seconds || 0) * 1000;
    const now = Date.now();
    let wall = now - started;
    if (timer.status === 'paused' && timer.paused_at) {
      const pausedAt = new Date(timer.paused_at).getTime();
      wall -= (now - pausedAt);
    }
    return Math.max(0, Math.floor((wall - totalPaused) / 1000));
  })();

  // Fetch active timer on auth ready
  const refresh = useCallback(async () => {
    if (!user) return null;
    try {
      const { data } = await axios.get(`${API}/timer/active`, { headers: authHeader() });
      setTimer(data);
      return data;
    } catch (e) {
      // Network/auth error — keep existing state
      return null;
    }
  }, [user]);

  useEffect(() => {
    if (user) refresh();
    else setTimer(null);
  }, [user, refresh]);

  // 1Hz re-render so the wall clock ticks; no DB hit.
  useEffect(() => {
    if (!timer || timer.status !== 'running') {
      if (tickRef.current) clearInterval(tickRef.current);
      tickRef.current = null;
      return;
    }
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => tickRef.current && clearInterval(tickRef.current);
  }, [timer?.status, timer?.id]);

  // Re-sync from server when tab gains focus (multi-device safety)
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refresh();
    });
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  const start = async (jobId, description = '') => {
    const { data } = await axios.post(`${API}/timer/start`,
      { job_id: jobId, description },
      { headers: authHeader() });
    setTimer(data);
    return data;
  };
  const pause = async () => {
    const { data } = await axios.post(`${API}/timer/pause`, {}, { headers: authHeader() });
    setTimer(data);
    return data;
  };
  const resume = async () => {
    const { data } = await axios.post(`${API}/timer/resume`, {}, { headers: authHeader() });
    setTimer(data);
    return data;
  };
  const updateActive = async (patch) => {
    const { data } = await axios.patch(`${API}/timer/active`, patch, { headers: authHeader() });
    setTimer((prev) => prev ? { ...prev, ...data } : data);
    return data;
  };
  const stop = async (description) => {
    const { data } = await axios.post(`${API}/timer/stop`,
      { description, rounding_minutes: 0 },
      { headers: authHeader() });
    setTimer(null);
    return data;
  };
  const discard = async () => {
    await axios.delete(`${API}/timer/active`, { headers: authHeader() });
    setTimer(null);
  };

  return (
    <TimerContext.Provider value={{
      timer, liveElapsedSeconds, tick,
      start, pause, resume, updateActive, stop, discard, refresh,
      isActive: !!timer && (timer.status === 'running' || timer.status === 'paused'),
    }}>
      {children}
    </TimerContext.Provider>
  );
};

export const formatHMS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};
